// PROJECT §4 Step 2 — Policy key-rules extraction review view.
//
// HR_Admin / HR_Viewer page that lists the rules the LLM extracted
// from a policy and lets an HR_Admin approve or reject each one
// individually. Edits to the rule text are in the same mutation so a
// single click commits the review. The page also surfaces the
// "blocks policy approval" derivation so HR can see at-a-glance whether
// the umbrella Approve affordance is unlocked yet.

import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PolicyExtractedRule, PolicyExtractionReport } from '@besideu/shared/policies';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { useSession } from '@/lib/auth.ts';

export function HrPolicyExtraction() {
  const { id = '' } = useParams<{ id: string }>();
  const session = useSession('hr-shell');
  const canReview = session.role === 'HR_Admin';

  const queryClient = useQueryClient();
  const report = useQuery({
    queryKey: ['policy-extraction', id],
    queryFn: () =>
      api.get<{ extraction: PolicyExtractionReport }>(`/api/hr/policies/${id}/extraction`),
    enabled: id !== '',
  });

  // Same polling posture as the audit detail page (in-flight pills flip
  // on the list view; this page is a refresh-driven deep dive).
  const review = useMutation({
    mutationFn: (input: {
      ruleId: string;
      decision: 'approve' | 'reject';
      ruleText?: string;
      reviewerNote?: string;
    }) => {
      const body: Record<string, string> = { decision: input.decision };
      if (input.ruleText !== undefined) body.ruleText = input.ruleText;
      if (input.reviewerNote !== undefined) body.reviewerNote = input.reviewerNote;
      return api.post<{
        ok: boolean;
        alreadyReviewed?: boolean;
        rule: PolicyExtractedRule;
        extraction: PolicyExtractionReport;
      }>(`/api/hr/policies/${id}/extraction/rules/${input.ruleId}/review`, body);
    },
    onSuccess: () => {
      // Two query keys: the per-policy detail (this page) and the HR
      // Policies list (the rule pill + approve-button disabled state
      // both depend on the rule counts, so a stale list would mislead
      // HR even though this page is up-to-date).
      void queryClient.invalidateQueries({ queryKey: ['policy-extraction', id] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });

  if (report.isPending) {
    return <p className="text-muted-foreground">Loading extraction…</p>;
  }
  if (report.error) {
    if (report.error instanceof ApiError && report.error.status === 404) {
      return <NotFound />;
    }
    return <p className="text-traffic-red">Could not load this extraction.</p>;
  }

  const data = report.data.extraction;
  return (
    <section className="space-y-6">
      <Link
        to="/hr/policies"
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to policies
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Key-rules review</h1>
        <p className="text-sm text-muted-foreground">
          The system extracted the discrete rules Maeve would use to answer leave / pay questions.
          Review each rule and either approve it or reject it. Rejected rules are excluded from
          Maeve&rsquo;s policy surface. The umbrella policy approval unlocks once every rule is in a
          terminal state.
        </p>
        <StatusBanner
          status={data.status}
          lastError={data.lastError}
          processedAt={data.processedAt}
          summary={data.summary}
          blocksPolicyApproval={data.blocksPolicyApproval}
        />
      </header>

      {data.status === 'Ready' && data.rules.length > 0 ? (
        <RulesList
          rules={data.rules}
          canReview={canReview}
          onReview={(ruleId, decision, ruleText, reviewerNote) => {
            const input: {
              ruleId: string;
              decision: 'approve' | 'reject';
              ruleText?: string;
              reviewerNote?: string;
            } = {
              ruleId,
              decision,
            };
            if (ruleText !== undefined) input.ruleText = ruleText;
            if (reviewerNote !== undefined) input.reviewerNote = reviewerNote;
            review.mutate(input);
          }}
          reviewingId={review.isPending ? (review.variables?.ruleId ?? null) : null}
        />
      ) : null}

      {/* Review mutation error. The server returns either `not_found` /
          `invalid_body` (4xx) or a Vertex failure (5xx); either way the
          user needs the message inline so they can act (refresh,
          re-try, or contact support). Cleared automatically when the
          next mutation succeeds because `reset()` is implicit in
          `onSuccess` — the hook's `error` field is dropped on the
          next attempt. */}
      {review.error ? (
        <p className="text-sm text-traffic-red" role="alert">
          {review.error instanceof ApiError
            ? review.error.message || 'Could not save your review.'
            : 'Could not save your review.'}
        </p>
      ) : null}
    </section>
  );
}

function StatusBanner({
  status,
  lastError,
  processedAt,
  summary,
  blocksPolicyApproval,
}: {
  status: PolicyExtractionReport['status'];
  lastError: string | null;
  processedAt: string | null;
  summary: PolicyExtractionReport['summary'];
  blocksPolicyApproval: boolean;
}) {
  if (status === 'Pending' || status === 'Processing') {
    return (
      <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        The system is still extracting rules. Refresh in a moment.
      </p>
    );
  }
  if (status === 'Unavailable') {
    return (
      <p className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 px-3 py-2 text-sm text-traffic-amber-text">
        Key-rule extraction isn&rsquo;t available yet. A besideU adviser needs to sign off the
        extraction prompt before rules can be generated.
      </p>
    );
  }
  if (status === 'Failed') {
    return (
      <p className="rounded-md border border-traffic-red/40 bg-traffic-red/10 px-3 py-2 text-sm text-traffic-red">
        The extraction didn&rsquo;t finish.
        {lastError ? <span className="ml-1 font-mono text-xs">{lastError}</span> : null}
      </p>
    );
  }
  if (status === 'NoRules') {
    return (
      <p className="text-sm text-muted-foreground">
        The system didn&rsquo;t surface any extractable rules from this document. You can approve
        the policy directly from the Policies page.
      </p>
    );
  }
  // Ready
  return (
    <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
      <p>
        Extracted {summary.total} rule{summary.total === 1 ? '' : 's'}.{' '}
        {summary.approved > 0 ? (
          <span className="text-traffic-green-text">{summary.approved} approved</span>
        ) : null}
        {summary.rejected > 0 ? (
          <span className="ml-2 text-muted-foreground">{summary.rejected} rejected</span>
        ) : null}
        {summary.pendingReview > 0 ? (
          <span className="ml-2 text-traffic-amber-text">
            {summary.pendingReview} pending review
          </span>
        ) : null}
      </p>
      <p className="text-xs text-muted-foreground">
        {processedAt ? `Generated ${new Date(processedAt).toLocaleString('en-GB')}. ` : null}
        {blocksPolicyApproval
          ? 'Approving the policy is blocked until the pending review is complete.'
          : 'Policy approval is unblocked.'}
      </p>
    </div>
  );
}

function RulesList({
  rules,
  canReview,
  onReview,
  reviewingId,
}: {
  rules: PolicyExtractedRule[];
  canReview: boolean;
  onReview: (
    ruleId: string,
    decision: 'approve' | 'reject',
    ruleText?: string,
    reviewerNote?: string,
  ) => void;
  reviewingId: string | null;
}) {
  return (
    <ul className="space-y-3">
      {rules.map((rule) => (
        <li key={rule.id} className="rounded-lg border border-border p-4">
          <RuleRow
            rule={rule}
            canReview={canReview}
            disabled={reviewingId === rule.id}
            onReview={onReview}
          />
        </li>
      ))}
    </ul>
  );
}

function RuleRow({
  rule,
  canReview,
  disabled,
  onReview,
}: {
  rule: PolicyExtractedRule;
  canReview: boolean;
  disabled: boolean;
  onReview: (
    ruleId: string,
    decision: 'approve' | 'reject',
    ruleText?: string,
    reviewerNote?: string,
  ) => void;
}) {
  const isTerminal = rule.status === 'Approved' || rule.status === 'Rejected';
  const [editing, setEditing] = useState(false);
  const [textDraft, setTextDraft] = useState(rule.ruleText ?? '');
  const [noteDraft, setNoteDraft] = useState(rule.reviewerNote ?? '');

  function submit(decision: 'approve' | 'reject', e?: FormEvent) {
    e?.preventDefault();
    const trimmed = textDraft.trim();
    const note = noteDraft.trim();
    onReview(
      rule.id,
      decision,
      trimmed === '' ? undefined : trimmed,
      note === '' ? undefined : note,
    );
    setEditing(false);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <StatusPill status={rule.status} />
          <span className="font-mono text-xs text-muted-foreground">{rule.ruleKey}</span>
          {rule.category ? (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {rule.category}
            </span>
          ) : null}
        </div>
        {rule.reviewedByUserName ? (
          <span className="text-xs text-muted-foreground">
            {rule.status === 'Approved' ? 'Approved' : 'Rejected'} by {rule.reviewedByUserName}
            {rule.reviewedAt ? ` · ${new Date(rule.reviewedAt).toLocaleString('en-GB')}` : null}
          </span>
        ) : null}
      </div>

      {editing && canReview ? (
        <form className="space-y-3" onSubmit={(e) => submit('approve', e)}>
          <label className="block">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">Rule text</span>
            <textarea
              required
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-base text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Reviewer note (optional)
            </span>
            <input
              type="text"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-base text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {/* The Approve button is type="submit" so the form's
                onSubmit handler runs `submit('approve', e)`. No onClick
                is needed (and a no-op onClick would shadow the form
                submit). */}
            <Button type="submit" variant="primary" loading={disabled}>
              Approve
            </Button>
            <Button
              type="button"
              variant="secondary"
              loading={disabled}
              onClick={(e) => submit('reject', e)}
            >
              Reject
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setTextDraft(rule.ruleText ?? '');
                setNoteDraft(rule.reviewerNote ?? '');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p className="text-sm whitespace-pre-wrap">{rule.ruleText ?? '—'}</p>
          {rule.policyExcerpt ? (
            <blockquote className="border-l-2 border-border pl-3 text-xs text-muted-foreground italic">
              {rule.policyExcerpt}
            </blockquote>
          ) : null}
          {rule.reviewerNote ? (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Reviewer note:</span> {rule.reviewerNote}
            </p>
          ) : null}
          {canReview && !isTerminal ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                onClick={() => onReview(rule.id, 'approve')}
                loading={disabled}
              >
                Approve
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onReview(rule.id, 'reject')}
                loading={disabled}
              >
                Reject
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setTextDraft(rule.ruleText ?? '');
                  setNoteDraft(rule.reviewerNote ?? '');
                  setEditing(true);
                }}
              >
                Edit &amp; review
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: PolicyExtractedRule['status'] }) {
  const cls =
    status === 'Approved'
      ? 'bg-traffic-green/20 text-traffic-green-text'
      : status === 'Rejected'
        ? 'bg-traffic-red/20 text-traffic-red'
        : 'bg-traffic-amber/20 text-traffic-amber-text';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}>
      {status === 'Pending_Review' ? 'Pending review' : status}
    </span>
  );
}

function NotFound() {
  return (
    <section className="space-y-3">
      <h1 className="text-2xl font-semibold">Extraction not found</h1>
      <p className="text-muted-foreground">
        Either this policy doesn&rsquo;t have an extraction yet, or it doesn&rsquo;t exist in your
        organisation.
      </p>
      <Link to="/hr/policies" className="text-sm text-primary underline-offset-4 hover:underline">
        ← Back to policies
      </Link>
    </section>
  );
}
